import { FC, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Dropdown, Menu, Space, Typography } from 'antd';
import { TbCheck, TbChevronDown, TbCrosshair, TbEye, TbSearch, TbSettings } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useSubjectWatchlists, useToggleWatchlistSubject } from '@/queries';
import { DropdownMenuLabel, DropdownPanel, DropdownPanelMenu, EmptySection } from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { SubjectReference } from '@/types';

import WorkspacePrimitiveEditorModal from '../../modals/ResourceCreationModals/WorkspacePrimitiveEditorModal';

import './index.css';

interface AddToWatchlistControlProps {
    disabled?: boolean;
    subject: SubjectReference;
}

const AddToWatchlistControl: FC<AddToWatchlistControlProps> = ({ disabled = false, subject }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const navigate = useNavigate();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [search, setSearch] = useState('');
    const watchlistsQuery = useSubjectWatchlists(subject);
    const toggleSubject = useToggleWatchlistSubject(subject);
    const watchlists = useMemo(
        () => (watchlistsQuery.data ?? []).filter((watchlist) => !watchlist.archived),
        [watchlistsQuery.data],
    );
    const selectedCount = watchlists.filter((watchlist) => watchlist.selected).length;
    const filteredWatchlists = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();

        return normalizedSearch
            ? watchlists.filter((watchlist) => watchlist.title.toLowerCase().includes(normalizedSearch))
            : watchlists;
    }, [search, watchlists]);

    const toggleWatchlist = async (watchlistId: string) => {
        if (toggleSubject.isPending) {
            return;
        }

        const watchlist = watchlists.find((candidate) => candidate.id === watchlistId);

        if (!watchlist || !watchlist.canEdit || watchlist.locked) {
            return;
        }

        try {
            await toggleSubject.mutateAsync({
                itemId: watchlist.itemId,
                selected: watchlist.selected,
                watchlistId,
            });
        } catch (error) {
            captureException(error);
            toast.error('Unable to update this watchlist.');
        }
    };

    const primaryAction = () => {
        const unselected = watchlists.filter(
            (watchlist) => !watchlist.selected && watchlist.canEdit && !watchlist.locked,
        );

        if (unselected.length === 1) {
            void toggleWatchlist(unselected[0].id);
        } else {
            setDropdownOpen(true);
        }
    };

    return (
        <>
            <Space.Compact size='small'>
                <Button
                    className='add-to-watchlist-primary-button'
                    disabled={disabled || watchlistsQuery.isLoading || toggleSubject.isPending}
                    icon={
                        <span
                            aria-hidden='true'
                            data-add-to-watchlist-icon='true'
                            style={{
                                alignItems: 'center',
                                display: 'inline-flex',
                                height: '1rem',
                                justifyContent: 'center',
                                lineHeight: 0,
                                transform: 'translateY(-0.5px)',
                                width: '1rem',
                            }}
                        >
                            <TbEye size='1rem' style={{ display: 'block' }} />
                        </span>
                    }
                    loading={toggleSubject.isPending}
                    size='small'
                    style={{ alignItems: 'center', display: 'inline-flex' }}
                    onClick={primaryAction}
                >
                    {selectedCount > 0
                        ? `${selectedCount} Watchlist${selectedCount === 1 ? '' : 's'}`
                        : 'Add to Watchlist'}
                </Button>
                <Dropdown
                    disabled={disabled || toggleSubject.isPending}
                    open={dropdownOpen}
                    placement='bottomRight'
                    trigger={['click']}
                    onOpenChange={(nextOpen) => {
                        setDropdownOpen(nextOpen);
                        if (!nextOpen) {
                            setSearch('');
                        }
                    }}
                    popupRender={() => (
                        <DropdownPanel
                            actions={
                                <DropdownPanelMenu selectable={false}>
                                    <Menu.Item
                                        disabled={toggleSubject.isPending}
                                        icon={<TbEye size='1rem' />}
                                        key='create-watchlist'
                                        onClick={() => {
                                            setDropdownOpen(false);
                                            setCreateOpen(true);
                                        }}
                                    >
                                        Create New Watchlist
                                    </Menu.Item>
                                    <Menu.Item
                                        disabled={toggleSubject.isPending}
                                        icon={<TbSettings size='1rem' />}
                                        key='manage-watchlists'
                                        onClick={() => navigate('/settings/account/preferences/watchlists')}
                                    >
                                        Manage Watchlists
                                    </Menu.Item>
                                </DropdownPanelMenu>
                            }
                            search={{ placeholder: 'Search Watchlists...', setValue: setSearch, value: search }}
                            width={16 * 24}
                        >
                            <DropdownMenuLabel icon={<TbCrosshair size='0.875rem' />}>
                                {watchlists.length} Watchlist{watchlists.length === 1 ? '' : 's'}
                            </DropdownMenuLabel>
                            {watchlistsQuery.isLoading ? (
                                <Typography.Paragraph style={{ padding: '1rem', color: token.colorTextTertiary }}>
                                    Loading watchlists…
                                </Typography.Paragraph>
                            ) : filteredWatchlists.length ? (
                                <DropdownPanelMenu selectable={false}>
                                    {filteredWatchlists.map((watchlist) => (
                                        <Menu.Item
                                            disabled={!watchlist.canEdit || watchlist.locked || toggleSubject.isPending}
                                            icon={
                                                watchlist.selected ? (
                                                    <TbCheck size='1rem' style={{ color: token.colorPrimary }} />
                                                ) : (
                                                    <TbCrosshair
                                                        size='1rem'
                                                        style={{ color: token.colorTextTertiary }}
                                                    />
                                                )
                                            }
                                            key={watchlist.id}
                                            onClick={() => void toggleWatchlist(watchlist.id)}
                                        >
                                            {watchlist.title}
                                            <br />
                                            <small style={{ color: token.colorTextTertiary }}>
                                                {watchlist.itemCount} items · {watchlist.visibility}
                                            </small>
                                        </Menu.Item>
                                    ))}
                                </DropdownPanelMenu>
                            ) : (
                                <EmptySection
                                    description={
                                        search
                                            ? 'No watchlists match your search.'
                                            : 'Create a watchlist to start tracking this company.'
                                    }
                                    height='12rem'
                                    icon={<TbSearch size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                                    label={search ? 'No Watchlists Found' : 'No Watchlists Yet'}
                                />
                            )}
                        </DropdownPanel>
                    )}
                >
                    <Button
                        aria-label='Choose watchlist'
                        disabled={disabled || toggleSubject.isPending}
                        icon={<TbChevronDown style={{ color: token.colorTextTertiary }} />}
                        size='small'
                    />
                </Dropdown>
            </Space.Compact>
            <WorkspacePrimitiveEditorModal
                errorMessage='Unable to create and add this watchlist.'
                kind='watchlist'
                open={createOpen}
                subjectsToAttach={[subject]}
                submitLabel='Create and Add'
                successMessage='Watchlist created and added.'
                onClose={() => setCreateOpen(false)}
            />
        </>
    );
};

export default AddToWatchlistControl;
